import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useConfirmDialog from '@/components/ConfirmDialogProvider';
import { Page } from '@/declarations/models/Page';
import { PageContent } from '@/declarations/models/PageContent';
import { Status } from '@/declarations/models/Status';
import { Api } from '@/services/Api';
import { useEditorModel } from '../lib/components/EditorDataProvider';
import { usePageEditorFormState } from './PageEditorFormStateProvider';
import { preparePageForSave } from './preparePageForSave';

export interface RestoreSource {
  /** Describes what is restored, e.g. "revisjon 1234 (20. okt. 2026, 14:05)" */
  label: string;
  /** Loads the content to restore */
  loadContent: () => Promise<PageContent | null | undefined>;
}

/**
 * Restores the page's content from an earlier revision or version by creating a new draft version with that content.
 * Page level fields (path, title, main image etc.) keep their current values.
 * @param onRestored Called after the new draft has been created, before the editor switches to it
 */
export function useRestorePageContent(onRestored?: () => void) {
  const { t: tComp } = useTranslation('components');
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirmDialog();
  const page = useEditorModel<Page>();
  const { isDirty } = usePageEditorFormState();
  const [, setSearchParams] = useSearchParams();

  return async (source: RestoreSource): Promise<void> => {
    if (!page.id) return;

    if (isDirty) {
      enqueueSnackbar(tComp('PageSettings.Restore.UnsavedChanges'), { variant: 'warning' });
      return;
    }

    // The backend refuses a new draft while a draft newer than the published version exists
    const currentVersions = await Api.getPageVersions(page.id).fetchDirect(null);
    if (!currentVersions) {
      enqueueSnackbar(tComp('PageSettings.Restore.Failed'), { variant: 'error' });
      return;
    }
    if (currentVersions.versions.some((v) => v.status === Status.DRAFT)) {
      enqueueSnackbar(tComp('PageSettings.Restore.DraftExists'), { variant: 'warning' });
      return;
    }

    const confirmed = await confirm(tComp('PageSettings.Restore.ConfirmText', { source: source.label }), {
      confirmLabel: tComp('PageSettings.Restore.Button'),
    });
    if (!confirmed) return;

    const content = await source.loadContent();
    if (!content) {
      enqueueSnackbar(tComp('PageSettings.Restore.Failed'), { variant: 'error' });
      return;
    }

    const savedPage = await Api.createPageDraft(page.id, { ...preparePageForSave(page), content }).fetchDirect(null);
    if (!savedPage?.active_version_id) {
      enqueueSnackbar(tComp('PageSettings.Restore.Failed'), { variant: 'error' });
      return;
    }

    // The new version gets the page title; name it after its source instead, so it can be traced later.
    // The backend serializes the version's content, so it has to be present.
    const renamed = await Api.savePageVersion(page.id, {
      id: savedPage.active_version_id,
      title: tComp('PageSettings.Restore.VersionTitle', { source: source.label }),
      content: {},
    }).fetchDirect(null);
    if (!renamed) {
      enqueueSnackbar(tComp('PageSettings.Restore.RenameFailed'), { variant: 'warning' });
    }

    enqueueSnackbar(tComp('PageSettings.Restore.Success'), { variant: 'success' });
    onRestored?.();
    setSearchParams({ versionId: savedPage.active_version_id.toString() });
  };
}
